import { CmpEventKindEnum } from "../../../../../graphql/types";
import { scrollDashboardToTop } from "../../../../../hooks/useScrollOnTabChange";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { eventDesignFont, eventDesignTheme } from "../../eventDesignTheme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { RoutePaths } from "../../../../../enumStrings";
import {
  CmpEventAddressProvenanceEnum,
  useGetCmpEventAddressOptionsQuery,
  useCreateCmpEventPreviewMutation,
} from "../../../../../graphql/types";
import { EventFormState, EventTypeOption } from "../../eventMockData";
import { appendScheduleReviewNote, normalizeEventButtonUrl } from "../../eventBackendMappers";
import EventScheduleDialog from "../../EventScheduleDialog";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { eventDateTimeToUtcMilli as dateTimeInputPartsToUtcMilli } from "../../eventDateTime";
import {
  EventTypeLibraryHandlers,
  WizardAction,
  WizardActionButton,
  WizardStepProps,
  normalizeEventDateTimeFields,
} from "./wizardShared";
import { ResultModal } from "../../EventSales/eventSalesShared";
import { usePageHeaderOverride } from "../../../../Layout/PageContainer";
import UnsavedChangesPrompt from "../../../../common/UnsavedChangesPrompt";
import { hasUnsavedFormChanges } from "../../../../common/formStateDirty";
import { StepProgress } from "../../../../common/dashboard/StepProgress";
import { FormPageShell } from "../../../../common/dashboard/FormPageShell";
import { DesignDialog } from "../../../../common/dashboard/DesignDialog";
import MissingRequiredFieldsDialog, {
  MissingRequiredFieldsRow,
  MissingRequiredFieldsVariant,
} from "../../../../common/dashboard/MissingRequiredFieldsDialog";
import Step1EventDetails from "./Step1EventDetails";
import Step2DateLocation from "./Step2DateLocation";
import Step3TicketSetup from "./Step3TicketSetup";
import Step4SponsorSetup from "./Step4SponsorSetup";
import type { EventAddressOption } from "./EventAddressSelector";
import { prepareEventDraftJson } from "../../eventDraftDocument";
import EventPreviewDialog from "./EventPreviewDialog";
import EventValidationBubble from "./EventValidationBubble";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { getUserFacingErrorText, STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";
import { enterFieldMessage, selectFieldMessage } from "../../../../../utils/fieldMessages";

const previewErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["BAD_USER_INPUT", "FORBIDDEN"],
});

// Step 4 is titled "Sponsor Set Up" everywhere; the step-4 screenshot's own
// stepper reads "Sponsors Set Up" but every other screenshot uses the
// singular, so the quirk policy normalizes on "Sponsor Set Up".
//
// footerTitle lets the footer nav labels differ from the stepper titles:
// the step-2 footer Next button binds "Ticket Set Up" — NO hyphen — per
// events-step2.md:129, and events-step3.md:222 orders the no-hyphen footer
// form reproduced verbatim (it also covers step 4's Previous label, which
// shows the real previous step name in its Q-011-corrected case).
const STEPS: {
  id: number;
  title: string;
  subtitle: string;
  footerTitle?: string;
}[] = [
  { id: 1, title: "Event Details", subtitle: "Basic information" },
  { id: 2, title: "Date & Location", subtitle: "When and where" },
  {
    id: 3,
    title: "Ticket - Set Up",
    subtitle: "Ticket types and pricing",
    footerTitle: "Ticket Set Up",
  },
  { id: 4, title: "Sponsor Set Up", subtitle: "Sponsors and packages" },
];

// Stepper items for the shared StepProgress (titles/subtitles VERBATIM from
// STEPS); the mock-bound "Your Event Planner / Coming Soon" fifth item renders
// via a numbered, non-interactive trailing item.
const stepProgressSteps = STEPS.map((stepItem) => ({
  title: stepItem.title,
  description: stepItem.subtitle,
}));

type EventWizardProps = EventTypeLibraryHandlers & {
  mode: "create" | "update";
  initialEvent: EventFormState;
  eventTypes: EventTypeOption[];
  chamberId: string;
  timeZone: string;
  eventAddressOptions?: readonly EventAddressOption[];
  saving?: boolean;
  allowStepJumpWithoutValidation?: boolean;
  // EV-N2: opened as a Duplicate Event copy that still needs its title/code.
  duplicated?: boolean;
  /** The step to open on (Create Event Save Draft continues on Update Event). */
  initialStep?: number;
  onSubmit: (args: {
    event: EventFormState;
    action: WizardAction;
    leaveAfterSave?: boolean;
    /** The wizard step the save was made from. */
    step?: number;
  }) => Promise<boolean>;
};

// Missing-field errors show the design's generic "Please complete the
// required fields." pop-up; any other validation error shows its own text.
const requiredFieldErrorKeys = new Set([
  "title", "eventType", "description", "heroShortDescription", "registrationDetailsHtml",
  "startDate", "startTime", "endDate", "endTime", "venueName", "address",
  "city", "province", "postalCode", "country", "virtualAccessInstructions",
]);
const summarizeValidationErrors = (stepErrors: Record<string, string>): string => {
  const otherMessage = Object.entries(stepErrors).find(
    ([key]) => !requiredFieldErrorKeys.has(key)
  )?.[1];
  return otherMessage || "Please complete the required fields.";
};

// Verbatim per-step subtitles, casing included (Claude Design (8) Create
// Event Screen.dc.html:1627-1630; step 4 has no trailing period).
const createStepDescriptions: Record<number, string> = {
  1: "To create an event, follow the four simple steps. Starting with Event Details.",
  2: "To create an event, follow the next step and set Date & Location.",
  3: "To create an event, follow the next steps and set Ticket - Set up.",
  4: "To create an event, follow the next steps and set Sponsor Set up",
};

const parseTicketPriceToCents = (value: string) => {
  const trimmedValue = value.trim();
  if (trimmedValue.startsWith("-")) return -1;
  return Math.round((Number(trimmedValue.replace(/[^0-9.]/g, "")) || 0) * 100);
};

// Rich-text fields store HTML, so "<p><br></p>" style leftovers must count
// as empty when validating required descriptions (EVT-PUBLISH-001).
const richTextToPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim();

// draftOptional: Save Draft keeps a real Draft event without it (Monday
// 13290832990); Publish, Schedule and Update still ask for it. invalid: the
// field is filled in but not valid (the pop-up says "fix", not "missing").
type StepIssue = { key: string; message: string; draftOptional?: boolean; invalid?: boolean };
type StepIssueFlags = { draftOptional?: boolean; invalid?: boolean };

// Every validation problem on one step, in form order. validateStep keeps the
// last message per field for the field highlights; the save bubble lists all.
// requireVisibility is set for Publish, Schedule and Update, never for Next or
// Save Draft on a Draft (Decision Q12). Save Draft on a Scheduled event keeps
// it Scheduled, so it gets the same checks as Schedule.
const collectStepIssues = (
  step: number,
  event: EventFormState,
  timeZone: string,
  requireVisibility = false
): StepIssue[] => {
  const issues: StepIssue[] = [];
  const add = (key: string, message: string, flags: StepIssueFlags = {}) => issues.push({ key, message, ...flags });
  const draftOptional = { draftOptional: true };
  const invalid = { invalid: true };
  const normalizedEvent =
    step === 2 ? normalizeEventDateTimeFields(event) : event;
  const community = normalizedEvent.eventKind === CmpEventKindEnum.Community;
  if (step === 1) {
    if (!normalizedEvent.title.trim()) add("title", enterFieldMessage("Event Title"));
    if (!normalizedEvent.eventTypeId) add("eventType", "Select an event type.");
    // Event Button Link and the teaser are optional (Create Event
    // Screen.dc.html:145, 170); a link that is entered must be valid.
    if (community && (normalizedEvent.externalEventUrl || "").trim()) { try { const url = new URL(normalizeEventButtonUrl(normalizedEvent.externalEventUrl)); if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) throw new Error(); } catch { add("externalEventUrl", "Enter a full web address for the Event Button Link, for example https://www.example.com.", invalid); } }
    if (!richTextToPlainText(normalizedEvent.registrationDetailsHtml || "")) add("registrationDetailsHtml", enterFieldMessage("Event Heading"), draftOptional);
    if (!richTextToPlainText(normalizedEvent.description)) {
      add("description", enterFieldMessage("Brief Event Description"));
    }
    // Community events hide Event Visibility (design :410), so it is only
    // required where the radios are shown.
    if (requireVisibility && !community && !normalizedEvent.visibility) add("visibility", "Under Event Visibility, select Public or Private.", draftOptional);
  }
  if (step === 2) {
    if (!normalizedEvent.startDate) add("startDate", selectFieldMessage("Start Date"));
    if (!normalizedEvent.endDate) add("endDate", selectFieldMessage("End Date"));
    if (!community && !normalizedEvent.startTime) add("startTime", selectFieldMessage("Start Time"));
    if (!community && !normalizedEvent.endTime) add("endTime", selectFieldMessage("End Time"));
    if (!community && !normalizedEvent.inPersonEnabled && !normalizedEvent.virtualEnabled) {
      // Claude Design (8) Create Event Screen.dc.html:573.
      add("format", "Select at least one event format", draftOptional);
    }
    if (!community && normalizedEvent.inPersonEnabled && !normalizedEvent.venueName.trim()) {
      add("venueName", "Enter the Venue Name for this in-person event.", draftOptional);
    }
    if (!community && normalizedEvent.inPersonEnabled && !normalizedEvent.address.trim()) {
      add("address", "Enter the Address for this in-person event.", draftOptional);
    }
    if (!community && normalizedEvent.inPersonEnabled) {
      // Messages use the on-screen labels (Step2DateLocation).
      const addressFieldMessages = {
        city: enterFieldMessage("City"),
        province: selectFieldMessage("Province/State"),
        postalCode: enterFieldMessage("Postal Code/Zip"),
        country: selectFieldMessage("Country"),
      } as const;
      for (const field of ["city", "province", "postalCode", "country"] as const) if (!(normalizedEvent[field] || "").trim()) add(field, addressFieldMessages[field], draftOptional);
    }
    if (
      !community && normalizedEvent.virtualEnabled &&
      !normalizedEvent.virtualAccessInstructions.trim()
    ) {
      add("virtualAccessInstructions", "Enter the Meeting URL for this virtual event.", draftOptional);
    }
    // Fully-empty agenda rows are already dropped by normalization; any row
    // left with only a time or only details is half-filled.
    const hasHalfFilledAgendaRow = (normalizedEvent.agendaItems || []).some(
      (agendaItem) => !agendaItem.timeLabel.trim() || !agendaItem.details.trim()
    );
    if (!community && hasHalfFilledAgendaRow) {
      add("agendaItems", "Each agenda row needs a time and details. Fill in both, or click Remove on that row.", invalid);
    }
    if (
      normalizedEvent.startDate &&
      (normalizedEvent.startTime || community) &&
      normalizedEvent.endDate &&
      (normalizedEvent.endTime || community)
    ) {
      const startAtUtcMilli = dateTimeInputPartsToUtcMilli(
        normalizedEvent.startDate,
        normalizedEvent.startTime || "00:00",
        timeZone
      );
      const endAtUtcMilli = dateTimeInputPartsToUtcMilli(
        normalizedEvent.endDate,
        normalizedEvent.endTime || "23:59",
        timeZone
      );
      if (!Number.isFinite(startAtUtcMilli) || !Number.isFinite(endAtUtcMilli)) {
        add("startDate", "We could not read the start or end date and time. Select the Start Date, Start Time, End Date and End Time again.", invalid);
      } else if (endAtUtcMilli <= startAtUtcMilli) {
        add("endTime", "The event must end after it starts. Change the End Date or End Time.", invalid);
      }
    }
  }
  if (step === 3 && !community && !normalizedEvent.eventIsFree) {
    // Create Event Screen.dc.html:740 wording (Step 3 also shows it inline).
    if (normalizedEvent.tickets.length === 0) {
      add("tickets", "Add at least one ticket type or mark the event as free.", draftOptional);
    }
    // Decision Q17: pre-check-in on with no 24/48/72 window blocks Next and
    // save; otherwise the backend saves 72 hours without asking.
    if ((normalizedEvent.preCheckInEnabled ?? true) && ![24, 48, 72].includes(Number(normalizedEvent.preCheckInHours))) {
      add("preCheckInHours", "Pre-Event Check-in is on. Select when to send the email: 24, 48 or 72 hrs prior. Or turn off Pre-Event Check-in.", draftOptional);
    }
    normalizedEvent.tickets.forEach((ticket, index) => {
      const ticketLabel = `Ticket ${index + 1}`;
      // Ticket Type Code is optional (design S1/ticket save rules); only the
      // name is required.
      if (!ticket.name.trim()) {
        add("tickets", `${ticketLabel} in the Ticket Types list has no name. Click Edit on that ticket and enter the Ticket Type Name.`);
      } else if (parseTicketPriceToCents(ticket.price) < 0) {
        add("tickets", `${ticketLabel} price must be $0.00 or greater.`, invalid);
      } else if (
        ticket.availabilityLimit &&
        (!Number.isFinite(Number(ticket.availabilityLimit)) ||
          Number(ticket.availabilityLimit) < 0 ||
          !Number.isInteger(Number(ticket.availabilityLimit)))
      ) {
        add("tickets", `${ticketLabel} availability must be a whole number 0 or greater.`, invalid);
      } else if (
        ticket.salesStartDate &&
        ticket.salesEndDate &&
        ticket.salesEndDate < ticket.salesStartDate
      ) {
        add("tickets", `${ticketLabel}: the Sales Period end date cannot be before the start date.`, invalid);
      }
    });
  }
  if (step === 4 && !community && normalizedEvent.sponsorshipEnabled) {
    const packages = normalizedEvent.sponsorshipPackages;
    if (packages.filter(item => item.heroBannerEnabled).length > 1) add("sponsorshipPackages", "Only one sponsor level can be the Title Sponsor. Edit the other sponsor levels and uncheck the Title Sponsor box.", invalid);
    packages.forEach((item, index) => {
      const label = item.name.trim() || `Sponsorship ${index + 1}`;
      // The package editor requires a limit and benefits. Imported packages
      // can have neither: a blank limit is unlimited and blank benefits are
      // allowed, so those packages must not block saving the event.
      const limit = String(item.maxAvailable ?? "").trim();
      // Sponsor Code is optional (Create Event Screen.dc.html:1411, 1757-1772).
      const missing = [
        !item.name.trim() && "a name",
        (!item.price.trim() || !Number.isFinite(Number(item.price)) || Number(item.price) < 0) && "a price of $0.00 or more",
        limit && (!Number.isInteger(Number(limit)) || Number(limit) <= 0) && "a whole-number limit above 0, or no limit",
      ].filter(Boolean);
      if (missing.length) {
        add("sponsorshipPackages", `${label} needs ${missing.join(", ")}.`, invalid);
      }
      if (Number(item.includedTickets) > 0 && !normalizedEvent.tickets.some(ticket => ticket.id === item.includedTicketTypeId)) {
        add("sponsorshipPackages", `${label} includes free tickets, but this event has no No Charge Sponsorship Ticket (NCST). In Ticket - Set Up, add a ticket with the code NCST. Or set Assign Event Tickets to 0 for ${label}.`, invalid);
      }
    });
  }
  return issues;
};

export const validateStep = (
  step: number,
  event: EventFormState,
  timeZone: string,
  requireVisibility = false
): Record<string, string> =>
  collectStepIssues(step, event, timeZone, requireVisibility).reduce<Record<string, string>>(
    (errors, issue) => ({ ...errors, [issue.key]: issue.message }),
    {}
  );

export type EventValidationStepIssues = { step: number; title: string; messages: string[] };

// All problems that block a save, grouped by wizard step (steps with none are
// left out). Community events only have steps 1 and 2.
export const getEventValidationIssuesByStep = (
  event: EventFormState,
  timeZone: string,
  requireVisibility = false
): EventValidationStepIssues[] =>
  STEPS.map((stepItem) => ({
    step: stepItem.id,
    title: stepItem.title,
    messages: collectStepIssues(stepItem.id, event, timeZone, requireVisibility).map((issue) => issue.message),
  })).filter((group) => group.messages.length > 0);

// Field names from Create Event Screen.dc.html (Step 1-4 labels).
const eventFieldLabels: Record<string, string> = {
  title: "Event Title", eventType: "Event Type", description: "Brief Event Description",
  registrationDetailsHtml: "Event Heading", visibility: "Event Visibility", externalEventUrl: "Event Button Link",
  startDate: "Start Date", startTime: "Start Time", endDate: "End Date", endTime: "End Time",
  format: "Event Format", venueName: "Venue Name", address: "Address", city: "City",
  province: "Province/State", postalCode: "Postal Code/Zip", country: "Country",
  virtualAccessInstructions: "Meeting URL", agendaItems: "Agenda", tickets: "Ticket Types",
  preCheckInHours: "Pre-Check-in", sponsorshipPackages: "Sponsorship Levels",
};

export type EventSaveDraftCheck = {
  rows: MissingRequiredFieldsRow[];
  errors: Record<string, string>;
  variant: MissingRequiredFieldsVariant;
  /** Wizard step of the first failed field; closing the pop-up goes there. */
  firstStep: number;
};

/**
 * Save Draft (Monday 13290832990) saves a real Draft event. A Draft needs the
 * event title, type, brief description and start/end date and time (the
 * backend minimum); entries that are filled in must be valid. A Scheduled (or
 * live) event stays as it is, so it gets the full checks. Returns null when
 * the event can be saved.
 */
export const getEventSaveDraftCheck = (
  event: EventFormState,
  timeZone: string,
  fullChecks = false
): EventSaveDraftCheck | null => {
  const issues = STEPS.flatMap((stepItem) =>
    collectStepIssues(stepItem.id, event, timeZone, fullChecks)
      .filter((issue) => fullChecks || !issue.draftOptional)
      .map((issue) => ({ ...issue, section: stepItem.title, step: stepItem.id })));
  if (!issues.length) return null;
  const rows = issues.map((issue) => ({
    section: issue.section,
    title: eventFieldLabels[issue.key] || issue.section,
    body: issue.message,
    invalid: issue.invalid,
  }));
  return {
    rows,
    errors: issues.reduce<Record<string, string>>((errors, issue) => ({ ...errors, [issue.key]: issue.message }), {}),
    // Only missing fields: MISSING REQUIRED FIELDS. Any entry that is not
    // valid: PLEASE FIX THE FOLLOWING (same shared pop-up).
    variant: rows.some((row) => row.invalid) ? "fixFollowing" : "missing",
    firstStep: issues[0].step,
  };
};

const EventWizard = ({
  mode,
  initialEvent,
  eventTypes,
  chamberId,
  timeZone,
  eventAddressOptions,
  saving,
  allowStepJumpWithoutValidation = false,
  duplicated = false,
  initialStep = 1,
  onSubmit,
  onAddEventType,
  onArchiveEventType,
}: EventWizardProps): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const [event, setEventState] = useState<EventFormState>(initialEvent);
  const [savedEvent, setSavedEvent] = useState<EventFormState>(initialEvent);
  const lastSeenInitialEventRef = useRef<EventFormState>(initialEvent);
  // Set when a newer saved event from the parent replaces the form. A save
  // that resolves after that must keep the parent's saved event as the
  // baseline (Oct 19 2026: "Leave site?" after "Event saved as draft.").
  const baselineReplacedRef = useRef(false);
  const pendingInitialEventRef = useRef<EventFormState | null>(null);
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const isCommunity = event.eventKind === CmpEventKindEnum.Community;
  const finalStep = isCommunity ? 2 : 4;
  // Design (15) CES:563, 717, 836, 926: a Published event shows only
  // "Save & Update Event" on every step.
  const isPublished = mode === "update" && savedEvent.status === "Published";
  const [step, setStep] = useState(() => Math.min(isCommunity ? 2 : 4, Math.max(1, initialStep)));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scheduleInfoOpen, setScheduleInfoOpen] = useState(() => new URLSearchParams(window.location.search).get("schedule") === "true");
  const [scheduleError, setScheduleError] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [saveIssues, setSaveIssues] = useState<{ action: WizardAction; groups: EventValidationStepIssues[] } | null>(null);
  const [draftCheck, setDraftCheck] = useState<EventSaveDraftCheck | null>(null);
  // Decision B8 (Oct 19 2026, design CES onSaveDraft): Save Draft on a
  // Scheduled event makes it a Draft, so it needs only the Draft minimum.
  // Save Draft keeps any other saved status (published or later), so that
  // save needs every field.
  const draftKeepsSavedStatus = mode === "update" && !["Draft", "Cancelled", "Scheduled"].includes(savedEvent.status);
  // The automatic saves (a ticket or sponsor pop-up save, Save & Update in
  // the UNSAVED CHANGES pop-up) update the event and keep a Scheduled event
  // scheduled; only the Save Draft button turns it into a Draft.
  const automaticSaveKeepsStatus = mode === "update" && !["Draft", "Cancelled"].includes(savedEvent.status);
  const automaticSaveAction: WizardAction = automaticSaveKeepsStatus ? "save" : "draft";
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [createPreview, { loading: previewLoading }] = useCreateCmpEventPreviewMutation({ context: previewErrorContext });
  const openPreview = async () => {
    setPreviewError("");
    try {
      const result = await createPreview({ variables: { input: {
        chamberId, eventId: event.id === "new-event" ? undefined : event.id,
        draftJson: await prepareEventDraftJson(event, timeZone, true),
      } } });
      const url = result.data?.createCmpEventPreview.previewUrl;
      if (!url) throw new Error("The event preview could not be opened.");
      const parsed = new URL(url);
      if (parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.pathname !== "/event-preview") throw new Error("The event preview address is invalid.");
      setPreviewUrl(url);
    } catch (error) { setPreviewError(getUserFacingErrorText(error, `Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`)); }
  };
  const [savingForNavigation, setSavingForNavigation] = useState(false);
  const { data: eventAddressOptionsData } = useGetCmpEventAddressOptionsQuery({
    // 100 is the backend maximum; Step 2's VenueCombo lists every venue A to Z.
    variables: { input: { chamberId, limit: 100 } },
    skip: !chamberId || eventAddressOptions !== undefined,
    fetchPolicy: "cache-and-network",
  });
  const resolvedEventAddressOptions = useMemo<readonly EventAddressOption[]>(() => {
    if (eventAddressOptions !== undefined) {
      return eventAddressOptions;
    }
    const result = eventAddressOptionsData?.getCmpEventAddressOptions;
    if (result?.manualEntryAllowed !== true) {
      return [];
    }

    return result.options.map((option) => ({
      key: option.key,
      address: option.address,
      provenance: option.provenance.map((value) =>
        value === CmpEventAddressProvenanceEnum.Recent ? "RECENT" : "SAVED"
      ),
    }));
  }, [eventAddressOptions, eventAddressOptionsData]);
  // The wizard can receive a newer saved baseline after it mounts. Queue that
  // value while the user has edits. Apply it only to a clean form so a user can
  // still restore the baseline that was visible when editing started.
  useEffect(() => {
    if (
      hasUnsavedFormChanges({
        baseline: lastSeenInitialEventRef.current,
        current: initialEvent,
      })
    ) {
      lastSeenInitialEventRef.current = initialEvent;
      pendingInitialEventRef.current = initialEvent;
    }

    if (
      hasUnsavedFormChanges({
        baseline: savedEvent,
        current: event,
      })
    ) {
      return;
    }

    const pendingInitialEvent = pendingInitialEventRef.current;
    if (!pendingInitialEvent) {
      return;
    }

    pendingInitialEventRef.current = null;
    baselineReplacedRef.current = true;
    setEventState(pendingInitialEvent);
    setSavedEvent(pendingInitialEvent);
    setErrors({});
  }, [event, initialEvent, savedEvent]);

  // Dirty-navigation guard (defect ledger SH-008/EVT-GLOBAL-002): compare the
  // editable event with the last saved value, not a transient prop reference.
  const hasUnsavedChanges = useMemo(
    () =>
      hasUnsavedFormChanges({
        baseline: savedEvent,
        current: event,
      }),
    [event, savedEvent]
  );
  const activeStep = STEPS.find((stepItem) => stepItem.id === step) || STEPS[0];

  const submit = useCallback(
    async (action: WizardAction, leaveAfterSave = false, eventOverride?: EventFormState) => {
      if (savingRef.current) return false;
      const normalizedEvent = normalizeEventDateTimeFields(eventOverride || event);
      if (action === "draft") {
        // Save Draft creates or updates a real Draft event: the MISSING
        // REQUIRED FIELDS pop-up names what it still needs.
        const check = getEventSaveDraftCheck(normalizedEvent, timeZone, draftKeepsSavedStatus);
        setEventState(normalizedEvent);
        setSaveIssues(null);
        if (check) {
          setErrors(check.errors);
          setDraftCheck(check);
          return false;
        }
      }
      const allErrors = action === "draft" ? {} : {
        ...validateStep(1, normalizedEvent, timeZone, true),
        ...validateStep(2, normalizedEvent, timeZone),
        ...validateStep(3, normalizedEvent, timeZone),
        ...validateStep(4, normalizedEvent, timeZone),
      };
      setEventState(normalizedEvent);
      if (Object.keys(allErrors).length > 0) {
        // Stay on this step: the bubble above the footer buttons lists every
        // problem by step, and the fields stay highlighted on each step.
        setErrors(allErrors);
        setSaveIssues({ action, groups: getEventValidationIssuesByStep(normalizedEvent, timeZone, true) });
        // The schedule dialog would cover the list.
        if (action === "schedule") setScheduleInfoOpen(false);
        return false;
      }
      setSaveIssues(null);
      setErrors({});
      setSavingForNavigation(leaveAfterSave);
      try {
        baselineReplacedRef.current = false;
        const saved = await onSubmit({
          event: normalizedEvent, action, step,
          ...(leaveAfterSave ? { leaveAfterSave: true } : {}),
        });
        // The parent may already have handed back the saved event (new
        // status, updatedAt) and the form shows it; keep that baseline.
        if (saved && !baselineReplacedRef.current) setSavedEvent(normalizedEvent);
        return saved;
      } finally {
        setSavingForNavigation(false);
      }
    },
    [draftKeepsSavedStatus, event, onSubmit, step, timeZone]
  );

  useEffect(() => {
    setPageHeaderOverride({
      withHeaderDivider: true,
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      headerSpacingClassName: "pt-[32px] pb-[16px] md:pt-[calc(32px-1rem)]",
      title: `${mode === "create" ? "Create Event" : "Update Event"} \u2013 ${
        activeStep.title
      }`,
      description:
        mode === "create"
          ? createStepDescriptions[step]
          // CES:2255 names the saved event, not the title being typed.
          : `You are updating ${savedEvent.title.trim() || "this event"}. Review each step and save your changes.`,
      titleClassName: "text-[28px] font-bold leading-tight text-[#0A0A0A]",
      titleStyle: { fontFamily: eventDesignFont },
      descriptionClassName: "mt-[6px] text-sm text-[#0A0A0A] whitespace-pre-line",
      descriptionStyle: { fontFamily: eventDesignFont, fontSize: 14 },

    });

    return () => setPageHeaderOverride(null);
  }, [activeStep.title, savedEvent.title, mode, setPageHeaderOverride, step]);

  const setEvent = (updater: (prev: EventFormState) => EventFormState) =>
    setEventState((prev) => savingRef.current ? prev : updater(prev));

  // Next validates the current step. A stepper click moves without a check
  // (Create Event Screen (15) CES:2294 onGo -> goStep, EV15-6); Publish and
  // Schedule still validate every step.
  const goToStep = (nextStep: number, validate = true) => {
    if (savingRef.current) return;
    const normalizedEvent = normalizeEventDateTimeFields(event);
    if (validate && nextStep > step && !allowStepJumpWithoutValidation) {
      const stepErrors = validateStep(step, normalizedEvent, timeZone);
      setEventState(normalizedEvent);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setValidationMessage(summarizeValidationErrors(stepErrors));
        return;
      }
    }
    setErrors({});
    setEventState(normalizedEvent);
    setStep(Math.min(finalStep, Math.max(1, nextStep)));
    scrollDashboardToTop();
  };

  // Design (16) / CEO Oct 12 2026: saving a ticket type or sponsor level
  // pop-up on an existing event saves the event at once ("Save & Update"),
  // so staff do not click Update Event again. It is the footer save with the
  // full checks; when a check fails the change stays in the form with the
  // errors shown and nothing is saved.
  const autoUpdatePendingRef = useRef(false);
  useEffect(() => {
    if (!autoUpdatePendingRef.current) return;
    autoUpdatePendingRef.current = false;
    void submit(automaticSaveAction, false, event);
  }, [automaticSaveAction, event, submit]);

  const saveOrUpdateButton = isPublished ? (
    <WizardActionButton
      tone="gold"
      disabled={saving}
      onClick={() => submit("save")}
      sx={{ minHeight: 40, px: "18px" }}
    >
      {saving ? "Updating..." : "Save & Update Event"}
    </WizardActionButton>
  ) : (
    <WizardActionButton tone="black" sx={{ backgroundColor: "#23272F", minHeight: 40, px: "18px" }} disabled={saving} onClick={() => submit("draft")}>
      Save Draft
    </WizardActionButton>
  );
  const previewButton = (
    <WizardActionButton tone="outline" disabled={previewLoading || saving} onClick={openPreview}>
      {previewLoading ? "Opening Preview..." : "Preview Event"}
    </WizardActionButton>
  );
  const scheduleButton = (
    <WizardActionButton tone="green" sx={{ backgroundColor: "#10B981", minHeight: 40, px: "18px" }} disabled={saving} onClick={() => { setScheduleError(""); setScheduleInfoOpen(true); }}>
      {isCommunity ? "Schedule Event" : "Schedule to Publish"}
    </WizardActionButton>
  );
  const publishButton = (
    <WizardActionButton
      tone="gold"
      disabled={saving}
      startIcon={
        saving ? (
          <CircularProgress color="inherit" size={16} />
        ) : isCommunity ? undefined : (
          <FileUploadOutlinedIcon />
        )
      }
      onClick={() => submit("publish")}
      sx={{ minHeight: 40, px: "18px" }}
    >
      Publish Event
    </WizardActionButton>
  );
  // Button order follows Create Event Screen CE:549, 703-706, 822, 899-900.
  const footerActions = step < finalStep ? (
    <>
      {saveOrUpdateButton}
      {/* Design (15) CES:564, 723, 837: black (#0A0A0A) with a "→" character. */}
      <WizardActionButton
        tone="black"
        onClick={() => goToStep(step + 1)}
        sx={{ backgroundColor: "#0A0A0A", minHeight: 40, px: "18px" }}
      >
        Next Step: {STEPS[step].footerTitle ?? STEPS[step].title} {"\u2192"}
      </WizardActionButton>
    </>
  ) : isCommunity ? (
    <>
      {saveOrUpdateButton}
      {previewButton}
      {isPublished ? null : <>{scheduleButton}{publishButton}</>}
    </>
  ) : (
    <>
      {previewButton}
      {isPublished ? saveOrUpdateButton : <>{saveOrUpdateButton}{scheduleButton}{publishButton}</>}
    </>
  );

  const stepProps: WizardStepProps = {
    event,
    setEvent,
    eventTypes,
    chamberId,
    eventAddressOptions: resolvedEventAddressOptions,
    errors,
    duplicated,
    onAddEventType,
    onArchiveEventType,
    // Design (16) autoUpdateEvent: only an existing event saves at once.
    onDialogSaved: mode === "update" ? () => { autoUpdatePendingRef.current = true; } : undefined,
  };

  return (
    <ThemeProvider theme={eventDesignTheme}><div style={{ fontFamily: eventDesignFont }}>
    <FormPageShell
      headingId={`event-wizard-step-${step}`}
      heading={activeStep.title}
      beforeMain={
        <UnsavedChangesPrompt
          // Design (16) (CEO, Oct 12 2026): "Save & Update" runs the same
          // save as the footer button (Save & Update Event, or Save Draft for
          // a draft) with the current form, with the full checks. It never
          // saves an older working copy.
          when={hasUnsavedChanges}
          saving={saving}
          onSave={() => submit(automaticSaveAction, true)}
          savedMessage={() => automaticSaveAction === "save"
            ? `Event ${event.title.trim() || "Untitled Event"} has been updated.`
            : "Event saved as draft."}
          savedHighlights={() => automaticSaveAction === "save" ? [event.title.trim() || "Untitled Event"] : []}
        />
      }
      stepper={
        <StepProgress
          variant="event"
          activeStep={step}
          ariaLabel={
            mode === "create" ? "Create event steps" : "Update event steps"
          }
          steps={stepProgressSteps.map((stepItem, index) =>
            isCommunity && index >= 2
              ? { ...stepItem, disabled: true, tooltip: "Not required for Community/Member Events." }
              : stepItem
          )}
          // Create Event Screen (15) CES:2293 tooltip on the disabled step 5.
          trailingItem={{ title: "Your Event Planner", description: "Coming Soon", number: 5, tooltip: "Available after the event is created." }}
          desktopGridColsClassName="lg:grid-cols-5"
          stepTestIdPrefix="event-step"
          // Design: steps are clickable without a check (EV15-6).
          onStepClick={(nextStep: number) => goToStep(nextStep, false)}
        />
      }
      contentClassName="space-y-[24px]"
      mainClassName="mx-auto w-full"
      mainPaddingTopClassName="pt-[24px]"
      actionBarClassName="mx-auto w-full"
      actionBar={
        <>
          {step === 1 ? (
            // Rendering as a link keeps the shared unsaved-changes interception
            // (UnsavedChangesPrompt watches anchor clicks) in the Cancel path.
            <WizardActionButton
              tone="outline"
              component={RouterLink}
              to={RoutePaths.chamberAdmin_events_manage}
              sx={{ fontSize: 13, minHeight: 40, borderRadius: "8px", borderColor: "#9E9E9E", px: "22px" }}
            >
              Cancel
            </WizardActionButton>
          ) : (
            <WizardActionButton
              tone="outline"
              startIcon={<ArrowBackIcon />}
              onClick={() => goToStep(step - 1)}
              sx={{ fontSize: 13, minHeight: 56, borderRadius: "10px", borderColor: "#ECECEC", px: "20px" }}
            >
              <span className="flex flex-col items-start text-left leading-tight">
                <span className="text-[12px] font-normal text-[#727272]">Previous:</span>
                <span className="text-[13px] font-semibold">
                  {/* Real previous step name; footerTitle carries the
                      spec-verbatim no-hyphen "Ticket Set Up" footer form
                      (events-step3.md:222). */}
                  {STEPS[step - 2].footerTitle ?? STEPS[step - 2].title}
                </span>
              </span>
            </WizardActionButton>
          )}
          <div className="relative flex flex-col gap-[12px] sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&_.MuiButton-root]:rounded-[8px] [&_.MuiButton-root]:text-[13px]">
            {saveIssues ? (
              <EventValidationBubble
                action={saveIssues.action}
                groups={saveIssues.groups}
                onClose={() => setSaveIssues(null)}
              />
            ) : null}
            {footerActions}
          </div>
        </>
      }
    >
      <DesignDialog
        // The navigation prompt already blocks edits while its save is pending.
        open={Boolean(saving) && !savingForNavigation}
        busy
        onClose={() => undefined}
        title="Saving event"
        message="Please wait while your images are prepared and your event is saved. Keep this page open."
      >
        <CircularProgress size={28} aria-label="Saving event" />
      </DesignDialog>
      {step === 1 ? <Step1EventDetails {...stepProps} /> : null}
      {step === 2 ? <Step2DateLocation {...stepProps} /> : null}
      {step === 3 ? <Step3TicketSetup {...stepProps} /> : null}
      {step === 4 ? <Step4SponsorSetup {...stepProps} /> : null}

      <EventPreviewDialog url={previewUrl} onClose={() => setPreviewUrl(null)} />
      <ResultModal open={!!previewError} success={false} title="The event preview could not be opened." message={previewError} onClose={() => setPreviewError("")} />
      <ActionSuccessDialog tone="error" message={validationMessage} onClose={() => setValidationMessage(null)} />
      <MissingRequiredFieldsDialog
        open={Boolean(draftCheck)}
        rows={draftCheck?.rows || []}
        variant={draftCheck?.variant}
        onClose={() => {
          // The pop-up then focuses the first failed field on that step.
          if (draftCheck && draftCheck.firstStep !== step) setStep(draftCheck.firstStep);
          setDraftCheck(null);
        }}
      />
      <EventScheduleDialog
        open={scheduleInfoOpen}
        eventName={event.title.trim()}
        timeZone={timeZone}
        saving={saving}
        error={scheduleError}
        onCancel={() => setScheduleInfoOpen(false)}
        onConfirm={async ({ publishAtUtcMilli, note }) => {
          const scheduledEvent = appendScheduleReviewNote({ ...event, publishAtUtcMilli }, note);
          if (await submit("schedule", false, scheduledEvent)) setScheduleInfoOpen(false);
        }}
      />
    </FormPageShell>
    </div></ThemeProvider>
  );
};

export default EventWizard;
